/**
 * `AbrechnungDokumente` — Reiter „Dokumente" der Abrechnungs-Unterseite (Antoine, 16.09.2026:
 * „Ich möchte auch alle Dokumente einsehen können", Mockup freigegeben mit „Ja, setz das so um").
 *
 * ## Was diese Ansicht ist — und was sie NICHT ist
 * Sie ist eine **aggregierende Leseansicht** über die Dokumente EINES Vorgangs, gruppiert nach
 * Herkunft. Sie legt keinen neuen Speicherort an, kopiert nichts und erzeugt nichts: jede Zeile
 * zeigt auf genau die Quelle, die es im System schon gibt —
 *   - **Angebot** → `GET /api/events/:id/offers` + `openPrintableDocument('/api/offers/:id/document')`
 *     (dieselbe Route, die `pages/event-detail.tsx` für „Dokument" nutzt). Das Angebots-Dokument
 *     wird bei Aufruf LIVE gerendert, es liegt kein PDF im Archiv — die Kopfzeile der Gruppe sagt
 *     das ausdrücklich, damit niemand ein eingefrorenes Archivdokument erwartet.
 *   - **Kostenübernahme** → dieselbe Owner/Admin-geschützte Route wie die Übersicht
 *     (`openAuthenticatedFile('/api/events/:id/cost-acceptance/pdf')`, s.
 *     `KostenuebernahmeDetail`). Kein zweiter, offener Weg an der Rechteprüfung vorbei: der
 *     Download läuft über denselben authentifizierten Aufruf, der Server entscheidet.
 *   - **Rechnungen** → `invoicesQ` der Seite, bewusst INKLUSIVE stornierter Belege (die Übersicht
 *     zeigt sie auch, aber der Beleg-Verlauf ist hier der Zweck: ein Papierpfad mit Lücke ist
 *     wertlos).
 *   - **Anfrage-Anhänge** → `event.details.attachments` + `/api/events/:id/attachments/:id`,
 *     dieselbe Quelle wie die Karte „Anhänge" auf der Vorgangsseite. Gespiegelt, nicht kopiert.
 *   - **Mail-Anhänge** → `GET /api/events/:id/emails`, Feld `attachmentsManifest`. Für eingehende
 *     Post speichert MAESTRO laut Migration 317 ausschließlich METADATEN, nie die Bytes. Diese
 *     Zeilen sind deshalb bewusst NICHT herunterladbar und sagen das auch — ein Download-Knopf,
 *     der zwangsläufig ins Leere liefe, wäre eine Lüge in Knopfform.
 *
 * ## Datenherkunft / Ladeverhalten
 * `invoices`, `costAcceptance` und `event` kommen als Props von der Seite (dort ohnehin geladen).
 * Nur die zwei Abfragen, die die Übersicht nicht braucht (Angebotsvarianten, E-Mail-Verlauf),
 * laufen HIER — die Komponente wird ausschließlich gerendert, wenn der Reiter „Dokumente" aktiv
 * ist, also stellt der Reiter „Übersicht" exakt dieselben Anfragen wie vorher. Ein neuer,
 * aggregierender Endpunkt war nicht nötig.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useApi } from "../../hooks/useApi";
import { openAuthenticatedFile } from "../../lib/files";
import { openPrintableDocument } from "../../lib/print";
import { rechnungsZustand, type ZustandsTon } from "../../lib/rechnungs-zustand";
import { Card, EmptyState, ErrorNote, Icon, Input, LoadingRows } from "../components";
import {
  attachmentIcon,
  attachmentTypLabel,
  formatBytes,
  formatDate,
  formatDateTime,
  money,
  INVOICE_TYPE_LABEL,
  PILL_CLASSES,
} from "../format";
import type { AttachmentManifestEntry, EventAttachment, EventRow, Invoice, OfferOption } from "../types";
import { caMeta, type CostAcceptance } from "./KostenuebernahmeDetail";

/** Antwortform von `GET /api/events/:id/emails` — nur die Felder, die diese Ansicht liest. */
interface MailZeile {
  id: string;
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  attachmentsManifest: AttachmentManifestEntry[] | null;
  receivedAt: string;
}

/** Ein Mail-Anhang mit seiner Nachricht — flachgezogen für die Liste. */
interface MailAnhang {
  schluessel: string;
  eintrag: AttachmentManifestEntry;
  mail: MailZeile;
}

type Gruppe = "angebot" | "kostenuebernahme" | "rechnungen" | "anhaenge";

const TON_CLS: Record<ZustandsTon, string> = {
  neutral: PILL_CLASSES.neutral,
  warn: PILL_CLASSES.warn,
  success: PILL_CLASSES.success,
  danger: PILL_CLASSES.danger,
};

const PILLE = "inline-flex items-center rounded-full px-2 py-0.5 font-label-md text-[10.5px]";
const AKTION = "inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline disabled:opacity-50";
const EYEBROW = "font-label-md text-[10.5px] font-semibold uppercase tracking-[0.07em] text-secondary dark:text-secondary-fixed-dim";

/** Eine Dokumentzeile: Icon-Kachel · Titel/Meta · Aktionen rechts. Gleiches Raster wie die
 *  Zeilen der Übersicht (34px-Kachel, `sm:`-Spalte für die Aktionen). */
function DokumentZeile({
  icon,
  gedaempft,
  titel,
  meta,
  aktionen,
}: {
  icon: string;
  gedaempft?: boolean;
  titel: ReactNode;
  meta: ReactNode;
  aktionen?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
      <div
        className={`grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim ${gedaempft ? "opacity-55" : ""}`}
      >
        <Icon name={icon} className="text-[18px]" />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
          {titel}
        </div>
        <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{meta}</p>
      </div>
      {aktionen && (
        <div className="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 sm:col-span-1 sm:justify-end">{aktionen}</div>
      )}
    </div>
  );
}

function Abschnitt({ titel, hinweis, children }: { titel: string; hinweis?: string; children: ReactNode }) {
  return (
    <section className="border-b border-outline-variant/30 py-4 last:border-b-0 dark:border-outline/20">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className={EYEBROW}>{titel}</span>
        {hinweis && <span className="max-w-[60ch] font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">{hinweis}</span>}
      </div>
      <div className="mt-1 divide-y divide-outline-variant/20 dark:divide-outline/10">{children}</div>
    </section>
  );
}

/** Erklär-Kasten unter einer Gruppe (warum gespiegelt / warum kein Download). */
function Fussnote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 flex items-start gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-low p-2.5 font-body-sm text-[11.5px] text-secondary dark:border-outline/20 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
      <Icon name="info" className="mt-px shrink-0 text-[15px]" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

export function AbrechnungDokumente({
  eventId,
  event,
  invoices,
  costAcceptance,
}: {
  eventId: string;
  event: EventRow;
  /** Bereits auf der Seite geladen (`invoicesQ`) — inklusive stornierter Belege. */
  invoices: Invoice[];
  /** Bereits auf der Seite geladen (`caQ`). */
  costAcceptance: CostAcceptance | null | undefined;
}) {
  const [gruppe, setGruppe] = useState<Gruppe | "alle">("alle");
  const [suche, setSuche] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  const offersQ = useApi<OfferOption[]>(`/api/events/${eventId}/offers`);
  const mailsQ = useApi<MailZeile[]>(`/api/events/${eventId}/emails`);

  const offers = useMemo(() => (offersQ.data ?? []).filter((o) => o.isActive), [offersQ.data]);
  const chatAnhaenge = useMemo(
    () => ((event.details as { attachments?: EventAttachment[] } | null)?.attachments ?? []),
    [event.details],
  );
  const mailAnhaenge = useMemo<MailAnhang[]>(
    () =>
      (mailsQ.data ?? []).flatMap((m) =>
        (m.attachmentsManifest ?? []).map((eintrag, i) => ({ schluessel: `${m.id}-${i}`, eintrag, mail: m })),
      ),
    [mailsQ.data],
  );

  const ca = costAcceptance ?? null;
  const suchbegriff = suche.trim().toLowerCase();
  const passt = (...felder: (string | null | undefined)[]) =>
    suchbegriff === "" || felder.some((f) => (f ?? "").toLowerCase().includes(suchbegriff));

  const sichtbareOffers = offers.filter((o) => passt(o.title, o.label, "Angebot"));
  const sichtbareKa = ca && passt("Kostenübernahme", ca.signerName) ? ca : null;
  const sichtbareRechnungen = invoices.filter((inv) => passt(INVOICE_TYPE_LABEL[inv.invoiceType], inv.invoiceNumber));
  const sichtbareChatAnhaenge = chatAnhaenge.filter((a) => passt(attachmentTypLabel(a.contentType), "Anhang"));
  const sichtbareMailAnhaenge = mailAnhaenge.filter((a) => passt(a.eintrag.filename, a.mail.subject, a.mail.fromAddr));

  const zeigt = (g: Gruppe) => gruppe === "alle" || gruppe === g;
  const chips: { key: Gruppe | "alle"; label: string; anzahl: number }[] = [
    { key: "alle", label: "Alle", anzahl: offers.length + (ca ? 1 : 0) + invoices.length + chatAnhaenge.length + mailAnhaenge.length },
    { key: "angebot", label: "Angebot", anzahl: offers.length },
    { key: "kostenuebernahme", label: "Kostenübernahme", anzahl: ca ? 1 : 0 },
    { key: "rechnungen", label: "Rechnungen", anzahl: invoices.length },
    { key: "anhaenge", label: "Anhänge", anzahl: chatAnhaenge.length + mailAnhaenge.length },
  ];

  const sichtbareAnzahl =
    (zeigt("angebot") ? sichtbareOffers.length : 0) +
    (zeigt("kostenuebernahme") && sichtbareKa ? 1 : 0) +
    (zeigt("rechnungen") ? sichtbareRechnungen.length : 0) +
    (zeigt("anhaenge") ? sichtbareChatAnhaenge.length + sichtbareMailAnhaenge.length : 0);

  const laedt = offersQ.loading || mailsQ.loading;

  async function angebotsDokument(offerId: string) {
    setFehler(null);
    try {
      await openPrintableDocument(`/api/offers/${offerId}/document`);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }

  async function geschuetzteDatei(pfad: string) {
    setFehler(null);
    try {
      await openAuthenticatedFile(pfad);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      {fehler && <ErrorNote message={fehler} />}
      <Card className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                aria-pressed={gruppe === c.key}
                onClick={() => setGruppe(c.key)}
                className={`rounded-full px-3.5 py-1.5 font-label-md text-[11.5px] font-semibold ${
                  gruppe === c.key
                    ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                    : "border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
                }`}
              >
                {c.label} {c.anzahl}
              </button>
            ))}
          </div>
          <div className="group relative w-full max-w-xs">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
            />
            <Input
              className="!pl-10"
              aria-label="Dokument suchen"
              placeholder="Dokument suchen …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
          </div>
        </div>

        {(offersQ.error || mailsQ.error) && (
          <ErrorNote
            message={offersQ.error ?? mailsQ.error ?? ""}
            title="Ein Teil der Dokumente konnte nicht geladen werden"
            onRetry={() => {
              offersQ.reload();
              mailsQ.reload();
            }}
          />
        )}

        {laedt && <LoadingRows rows={3} />}

        {!laedt && sichtbareAnzahl === 0 ? (
          <EmptyState
            icon="folder_open"
            title="Keine Dokumente"
            hint={
              suchbegriff || gruppe !== "alle"
                ? "Für diese Auswahl gibt es nichts. Filter zurücksetzen oder anders suchen."
                : "Sobald ein Angebot gesendet, eine Kostenübernahme angefordert oder eine Rechnung erstellt wurde, steht sie hier."
            }
          />
        ) : (
          <div className="divide-y divide-outline-variant/30 dark:divide-outline/20">
            {/* ── Angebot ───────────────────────────────────────────────────────────────── */}
            {zeigt("angebot") && sichtbareOffers.length > 0 && (
              <Abschnitt
                titel="Angebot"
                hinweis="Wird bei Aufruf live gerendert — kein gespeichertes PDF, immer der aktuelle Stand."
              >
                {sichtbareOffers.map((o) => (
                  <DokumentZeile
                    key={o.id}
                    icon="description"
                    titel={
                      <>
                        <span>{o.title?.trim() || o.label}</span>
                        {o.isChosen && <span className={`${PILLE} ${PILL_CLASSES.success}`}>Gewählt</span>}
                        {o.isOutdated && <span className={`${PILLE} ${PILL_CLASSES.neutral}`}>Veraltet</span>}
                      </>
                    }
                    meta={
                      <>
                        v{o.version} · {o.guestCount} Gäste · {money(o.amountTotalCents)}
                        {o.chosenAt ? ` · angenommen ${formatDate(o.chosenAt)}` : ""}
                      </>
                    }
                    aktionen={
                      <>
                        <button type="button" className={AKTION} onClick={() => void angebotsDokument(o.id)}>
                          <Icon name="picture_as_pdf" className="text-[15px]" /> Dokument
                        </button>
                        {o.isChosen && event.publicToken && (
                          <a
                            href={`/angebot/${event.publicToken}`}
                            target="_blank"
                            rel="noreferrer"
                            className={AKTION}
                          >
                            <Icon name="open_in_new" className="text-[15px]" /> Angebotsseite
                          </a>
                        )}
                      </>
                    }
                  />
                ))}
              </Abschnitt>
            )}

            {/* ── Kostenübernahme ───────────────────────────────────────────────────────── */}
            {zeigt("kostenuebernahme") && sichtbareKa && (
              <Abschnitt
                titel="Kostenübernahme"
                hinweis="Archiviertes, signiertes Vertrags-PDF — der Download ist Inhabern vorbehalten."
              >
                <DokumentZeile
                  icon="draw"
                  gedaempft={sichtbareKa.status !== "signed"}
                  titel={
                    <>
                      <span>Kostenübernahme-Vertrag</span>
                      <span className={`${PILLE} ${caMeta(sichtbareKa.status).cls}`}>{caMeta(sichtbareKa.status).label}</span>
                    </>
                  }
                  meta={
                    <>
                      {sichtbareKa.signedAt ? `Signiert am ${formatDateTime(sichtbareKa.signedAt)}` : sichtbareKa.sentAt ? `Zugestellt am ${formatDateTime(sichtbareKa.sentAt)}` : `Angelegt am ${formatDateTime(sichtbareKa.createdAt)}`}
                      {sichtbareKa.signerName ? ` · ${sichtbareKa.signerName}` : ""}
                      {sichtbareKa.amountGrossCents != null ? ` · ${money(sichtbareKa.amountGrossCents)}` : ""}
                    </>
                  }
                  aktionen={
                    // Exakt dieselbe Reihenfolge/Bedingung wie `KostenuebernahmeDetail`: eigenes
                    // Archiv (owner/admin-geschützt) vor der ablaufenden Provider-URL. Ohne
                    // Signatur gibt es kein Dokument — dann bleibt die Zeile bewusst ohne Aktion.
                    sichtbareKa.status === "signed" && sichtbareKa.signedPdfSha256 ? (
                      <button
                        type="button"
                        className={AKTION}
                        onClick={() => void geschuetzteDatei(`/api/events/${eventId}/cost-acceptance/pdf`)}
                      >
                        <Icon name="picture_as_pdf" className="text-[15px]" /> Signierte Kostenübernahme (PDF)
                      </button>
                    ) : sichtbareKa.status === "signed" && sichtbareKa.signedPdfUrl ? (
                      <a href={sichtbareKa.signedPdfUrl} target="_blank" rel="noreferrer" className={AKTION}>
                        <Icon name="picture_as_pdf" className="text-[15px]" /> Signierte Kostenübernahme (PDF)
                      </a>
                    ) : (
                      <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                        Noch kein Dokument — erst nach der Signatur.
                      </span>
                    )
                  }
                />
              </Abschnitt>
            )}

            {/* ── Rechnungen ────────────────────────────────────────────────────────────── */}
            {zeigt("rechnungen") && sichtbareRechnungen.length > 0 && (
              <Abschnitt titel="Rechnungen" hinweis="Vollständiger Beleg-Verlauf inklusive stornierter Belege, aus LexOffice.">
                {sichtbareRechnungen.map((inv) => {
                  const zustand = rechnungsZustand(inv);
                  const storniert = inv.status === "voided";
                  return (
                    <DokumentZeile
                      key={inv.id}
                      icon="receipt_long"
                      gedaempft={storniert}
                      titel={
                        <>
                          <span className={storniert ? "line-through decoration-outline" : ""}>
                            {INVOICE_TYPE_LABEL[inv.invoiceType]}
                          </span>
                          <span className={`${PILLE} ${TON_CLS[zustand.ton]}`}>{zustand.label}</span>
                        </>
                      }
                      meta={
                        <>
                          {inv.invoiceNumber ?? "ohne Nummer"}
                          {inv.amountGrossCents != null ? ` · ${money(inv.amountGrossCents)}` : ""} · erstellt{" "}
                          {formatDate(inv.createdAt)}
                          {inv.sentAt ? ` · gesendet ${formatDate(inv.sentAt)}` : ""}
                        </>
                      }
                      aktionen={
                        inv.pdfUrl ? (
                          <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className={AKTION}>
                            <Icon name="picture_as_pdf" className="text-[15px]" /> PDF
                          </a>
                        ) : (
                          <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Kein PDF</span>
                        )
                      }
                    />
                  );
                })}
              </Abschnitt>
            )}

            {/* ── Anhänge aus der Anfrage ───────────────────────────────────────────────── */}
            {zeigt("anhaenge") && sichtbareChatAnhaenge.length > 0 && (
              <Abschnitt titel="Anhänge aus der Anfrage">
                {sichtbareChatAnhaenge.map((a) => (
                  <DokumentZeile
                    key={a.id}
                    icon={attachmentIcon(a.contentType)}
                    titel={<span>{attachmentTypLabel(a.contentType)}</span>}
                    meta={a.uploadedAt ? `Hochgeladen im Anfrage-Chat am ${formatDate(a.uploadedAt)}` : "Hochgeladen im Anfrage-Chat"}
                    aktionen={
                      <button
                        type="button"
                        className={AKTION}
                        onClick={() => void geschuetzteDatei(`/api/events/${eventId}/attachments/${a.id}`)}
                      >
                        <Icon name="visibility" className="text-[15px]" /> Ansehen
                      </button>
                    }
                  />
                ))}
                <Fussnote>
                  Dieselben Dateien wie in der Karte „Anhänge" auf der Vorgangsseite — hier nur gespiegelt, kein zweiter
                  Speicherort.
                </Fussnote>
              </Abschnitt>
            )}

            {/* ── Anhänge aus E-Mails (nur Metadaten, Migration 317) ────────────────────── */}
            {zeigt("anhaenge") && sichtbareMailAnhaenge.length > 0 && (
              <Abschnitt titel="Anhänge aus E-Mails">
                {sichtbareMailAnhaenge.map(({ schluessel, eintrag, mail }) => (
                  <DokumentZeile
                    key={schluessel}
                    icon={attachmentIcon(eintrag.mimeType)}
                    gedaempft
                    titel={
                      <>
                        <span>{eintrag.filename ?? "Anhang"}</span>
                        <span className={`${PILLE} ${PILL_CLASSES.neutral}`}>Nur Metadaten</span>
                      </>
                    }
                    meta={
                      <>
                        {formatBytes(eintrag.sizeBytes)} ·{" "}
                        {mail.direction === "inbound"
                          ? `eingegangen ${formatDate(mail.receivedAt)} von ${mail.fromAddr}`
                          : `gesendet ${formatDate(mail.receivedAt)}${mail.toAddr ? ` an ${mail.toAddr}` : ""}`}
                      </>
                    }
                    aktionen={
                      <span className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                        <Icon name="block" className="text-[15px]" /> Kein Download
                      </span>
                    }
                  />
                ))}
                <Fussnote>
                  Für eingehende Post speichert MAESTRO nur Dateiname, Typ und Größe — nie den Anhang-Inhalt selbst
                  (Migration 317). Ein echter Download wäre ein eigenes Vorhaben, kein Knopf an dieser Stelle.
                </Fussnote>
              </Abschnitt>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
